import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { vacancyService, applicationService } from '../../services/api';
import { getUserData } from '../../utils/auth';
import './CompanyVacancyDetail.css';

function CompanyVacancyDetail() {
  const { id } = useParams();
  const userData = getUserData();
  const companyId = userData?.companyId || userData?.CompanyId;

  const [vacancy, setVacancy] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id, companyId]);

  const loadData = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [vacanciesData, appsData] = await Promise.all([
        vacancyService.getVacanciesByCompany(companyId),
        applicationService.getApplicationsByCompany(companyId)
      ]);
      const v = vacanciesData.find((x) => x.Id === parseInt(id));
      setVacancy(v || null);
      const vacancyApps = (appsData || []).filter((a) => a.VacancyId === parseInt(id));
      setApplications(vacancyApps);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShortlist = async (app) => {
    try {
      await applicationService.updateApplicationReview({
        Id: app.Id,
        IsShortlisted: !app.IsShortlisted,
        CompanyNotes: app.CompanyNotes || '',
      });
      loadData();
    } catch (error) {
      alert('Error updating shortlist: ' + error.message);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
  const formatFileName = (path) => (path ? path.split(/[/\\]/).pop() : '-');

  if (!companyId) {
    return (
      <div className="company-vacancy-detail">
        <p>Company session not found. Please log in again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-vacancy-detail">
        <p>Loading vacancy details...</p>
      </div>
    );
  }

  if (!vacancy) {
    return (
      <div className="company-vacancy-detail">
        <p>Vacancy not found.</p>
        <Link to="/company-portal/my-vacancies">Back to My Vacancies</Link>
      </div>
    );
  }

  return (
    <div className="company-vacancy-detail">
      <div className="vacancy-detail-header">
        <Link to="/company-portal/my-vacancies" className="back-link">← Back to My Vacancies</Link>
        <h1>{vacancy.Title}</h1>
        {vacancy.PlacementDrive?.Title && (
          <p className="placement-info">Placement Drive: {vacancy.PlacementDrive.Title}</p>
        )}
        <div className="vacancy-meta">
          <span>📅 Posted: {formatDate(vacancy.Date)}</span>
          {vacancy.LastDate && <span>⏰ Last date: {formatDate(vacancy.LastDate)}</span>}
        </div>
      </div>
      {vacancy.Description && (
        <section className="vacancy-section">
          <h3>Description</h3>
          <p>{vacancy.Description}</p>
        </section>
      )}
      {vacancy.Conditions && (
        <section className="vacancy-section">
          <h3>Conditions / Eligibility</h3>
          <p>{vacancy.Conditions}</p>
        </section>
      )}

      <section className="applications-section">
        <h2>Applications ({applications.length})</h2>
        {applications.length === 0 ? (
          <p className="no-apps">No applications received for this vacancy yet.</p>
        ) : (
          <div className="applications-list">
            {applications.map((a) => (
              <div key={a.Id} className="app-card">
                <div className="app-header">
                  <div>
                    <strong>{a.Student?.Name}</strong> ({a.Student?.Code})
                  </div>
                  <span className={`shortlist-badge ${a.IsShortlisted ? 'yes' : 'no'}`}>
                    {a.IsShortlisted ? 'Shortlisted' : 'Not shortlisted'}
                  </span>
                </div>
                <div className="app-body">
                  <p><strong>Email:</strong> {a.Student?.Email}</p>
                  <p><strong>Mobile:</strong> {a.Student?.MobileNo}</p>
                  <p><strong>Applied:</strong> {formatDate(a.Date)}</p>
                  {a.Resume && (
                    <p>
                      <strong>Resume:</strong> {formatFileName(a.Resume)}
                      <button
                        type="button"
                        className="btn-download"
                        onClick={() => applicationService.downloadResume(a.Id, formatFileName(a.Resume))}
                      >
                        Download PDF
                      </button>
                    </p>
                  )}
                  {a.Description && <p><strong>Description:</strong> {a.Description}</p>}
                  {a.Answer1 && <p><strong>Answer 1:</strong> {a.Answer1}</p>}
                  {a.Answer2 && <p><strong>Answer 2:</strong> {a.Answer2}</p>}
                  {a.Answer3 && <p><strong>Answer 3:</strong> {a.Answer3}</p>}
                  {a.CompanyNotes && <p className="notes"><strong>Notes:</strong> {a.CompanyNotes}</p>}
                </div>
                <div className="app-actions">
                  <button
                    className={`btn-shortlist ${a.IsShortlisted ? 'shortlisted' : ''}`}
                    onClick={() => handleShortlist(a)}
                  >
                    {a.IsShortlisted ? 'Remove from shortlist' : 'Shortlist'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default CompanyVacancyDetail;
